import {
    Field,
    useFormikContext,
} from 'formik';
import {useEffect} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Container,
    Fieldset,
    Loader,
    Text,
    Title,
} from '@mantine/core';
import {
    FormikPasswordInput,
    FormikSelect,
    FormikSwitch,
    FormikTextInput,
} from '@flexinet/ui-components';

import {useDnsProvidersQuery} from '../hooks/useProviderConnectionsQuery';
import {ConnectFormValues} from './types';

// FNP-1248 — the CONNECT form fields.
//
// ⚠️ THE CREDENTIAL FIELD SWITCHES ON THE CHOSEN PROVIDER'S `credentialType`.
//
// Rendering both a role-ARN field and a token field, and letting the tenant
// pick, would let them submit a token for Route 53 — which the adapter rejects
// at connect time. That is a failure the form can simply prevent by never
// offering the wrong field. The catalog declares what each provider REQUIRES;
// this reads it and renders exactly that one input.

export const FormComponent = () => {

    const {
        data: providers,
        isLoading,
        isError,
    } = useDnsProvidersQuery();

    const {
        values,
        setFieldValue,
    } = useFormikContext<ConnectFormValues>();

    const selectedProvider = providers?.find(provider => provider.id === values.providerId);

    // ⚠️ THE DISCRIMINATOR IS MIRRORED INTO THE FORM STATE so the validation
    // schema can branch on it — Yup validates the values object, and it has no
    // access to the provider catalog.
    //
    // ⚠️ SWITCHING PROVIDER CLEARS BOTH CREDENTIAL FIELDS. Not just cosmetic: a
    // token typed for Cloudflare must never survive a switch to Route 53 and be
    // transmitted to the wrong vendor. The Yup schema strips the inapplicable
    // field as a second line of defence, but clearing here is what stops the
    // value from sitting in form state at all.
    useEffect(() => {
        const credentialType = selectedProvider?.credentialType ?? '';
        if (values.credentialType !== credentialType) {
            setFieldValue('credentialType', credentialType);
            setFieldValue('roleArn', '');
            setFieldValue('apiToken', '');
        }
    }, [selectedProvider?.credentialType, values.credentialType, setFieldValue]);

    return (
        <Container>
            <Fieldset
                legend={<Title order={3}><Text tt={'uppercase'}>{I18n.get('Provider')}</Text></Title>}
                px={'xl'}
                mb={'xl'}
            >
                {isLoading && <Loader size={'sm'}/>}

                {isError && (
                    <Alert color={'red'} title={I18n.get('Could not load the provider list')}>
                        {I18n.get('The list of DNS providers did not load, so there is nothing to connect to. Close this and try again.')}
                    </Alert>
                )}

                {!isLoading && !isError && (providers?.length ?? 0) === 0 && (
                    <Alert color={'yellow'} title={I18n.get('No providers available')}>
                        {I18n.get('Flexi has no DNS providers configured to connect to.')}
                    </Alert>
                )}

                {providers && providers.length > 0 && (
                    <>
                        <Field
                            name={'providerId'}
                            label={I18n.get('DNS provider')}
                            description={I18n.get('Which DNS host this connection is for.')}
                            component={FormikSelect}
                            withAsterisk
                            data={providers.map(provider => ({
                                value: provider.id,
                                // The catalog's human-readable name, never the id.
                                label: provider.name,
                            }))}
                        />

                        <Field
                            name={'displayName'}
                            label={I18n.get('Display name')}
                            description={I18n.get('Your own name for this connection, so several connections to the same provider are tellable apart — for example "Production AWS account".')}
                            component={FormikTextInput}
                            withAsterisk
                        />

                        {/* ─── ASSUMED_ROLE: the role ARN ─── */}
                        {selectedProvider?.credentialType === 'ASSUMED_ROLE' && (
                            <>
                                <Field
                                    name={'roleArn'}
                                    label={I18n.get('Role ARN')}
                                    description={I18n.get('The IAM role in YOUR AWS account that Flexi will assume.')}
                                    component={FormikTextInput}
                                    withAsterisk
                                    placeholder={'arn:aws:iam::123456789012:role/FlexiDnsAccess'}
                                />

                                {/* ⚠️ SETS THE EXPECTATION BEFORE THE SUBMIT, NOT AFTER.
                                    A first connection CANNOT succeed: Flexi mints the
                                    external id from the connection id, so it does not
                                    exist until the connection does, and the tenant
                                    cannot have put it in their trust policy yet.
                                    Saying so here means the not-active result that
                                    follows reads as the next step rather than a
                                    failure the tenant has to diagnose. */}
                                <Alert
                                    color={'blue'}
                                    variant={'light'}
                                    title={I18n.get('This takes two steps')}
                                    mt={'md'}
                                >
                                    <Text size={'sm'}>
                                        {I18n.get('When you save, Flexi generates an external ID for this connection and shows it to you. Add that ID to your role\'s trust policy, then re-validate. The connection will not be active until you do — that is expected, not an error.')}
                                    </Text>
                                </Alert>
                            </>
                        )}

                        {/* ─── API_TOKEN: the token ─── */}
                        {selectedProvider?.credentialType === 'API_TOKEN' && (
                            <Field
                                name={'apiToken'}
                                label={I18n.get('API token')}
                                description={I18n.get('The token issued by your provider. It is stored encrypted and is never shown again after you save.')}
                                // ⚠️ A PASSWORD INPUT, so the token is masked as it is
                                // typed and cannot be read off a shoulder or a shared
                                // screen. It is also never rendered back anywhere in
                                // this module — no output type can carry it.
                                component={FormikPasswordInput}
                                withAsterisk
                            />
                        )}

                        {/* ─── FNP-1233: THE DECLARED WRITE CAPABILITY ───

                            ⚠️ WORDED AS A CLAIM ABOUT THE TENANT'S CREDENTIAL, NOT AS
                            A FLEXI PERMISSION. "This role can create and delete zones"
                            is something the tenant KNOWS and is telling us. "Enable
                            writes" would be a switch someone ticks hopefully, and a
                            hopeful tick produces a connection claiming a capability the
                            credential does not have — failing at the first write
                            instead of at the declaration.

                            ⚠️ DEFAULTS OFF, set in initialValues. A connection nobody
                            deliberately marked writable must not be able to write. */}
                        {selectedProvider && (
                            <Field
                                name={'writable'}
                                label={selectedProvider.credentialType === 'ASSUMED_ROLE'
                                    ? I18n.get('This role can create and delete zones')
                                    : I18n.get('This token can create and delete zones')}
                                description={I18n.get('Leave this off if you granted read-only access. Flexi will not attempt any write through this connection unless you turn it on, and you can change it later by re-validating.')}
                                component={FormikSwitch}
                                mt={'md'}
                            />
                        )}
                    </>
                )}
            </Fieldset>
        </Container>
    );
};
